import React, { useEffect } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { SET_AUTHORISED } from '../../../../context/auth/authReducer/authReducer'

import { FieldInput, Loader, FieldError, PercentButton } from '@percent/admin-dashboard/common/components'
import { useMutation, useAuthDispatch, useAuthState } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AuthView } from '@percent/admin-dashboard/app/auth/authview/AuthView'
import { Box, Text } from '@percent/lemonade'
import styles from './LoginWithTwoFA.module.scss'

export function LoginWithTwoFA() {
  const dispatch = useAuthDispatch()
  const { isAuthorised } = useAuthState()
  const { authService } = useServices()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state, search } = useLocation()

  useEffect(() => {
    if (!state?.password) {
      navigate('/login')
    }
  }, [state, navigate])

  useEffect(() => {
    if (!isAuthorised) return

    const redirectTo = new URLSearchParams(search).get('redirectTo')

    if (redirectTo) navigate(redirectTo)
    else navigate('/organizations')
  }, [isAuthorised, navigate, search])

  const [{ errorMessage, isLoading }, { apiFunc: loginUser, setErrorMessage }] = useMutation(
    authService.verifyOtpKey,
    ({ data: { data } }) => {
      localStorage.authState = JSON.stringify({
        isAuthorised: true,
        isAuthorising: false
      })

      dispatch({
        type: SET_AUTHORISED,
        user: data
      })
    }
  )

  const { values, errors, handleBlur, handleSubmit, touched, setFieldValue } = useFormik({
    initialValues: {
      token: ''
    },
    validationSchema: () =>
      yup.object().shape({
        token: yup
          .string()
          .required('Required')
          .matches(/^[0-9]+$/, t('errorMessage.onlyDigits'))
          .min(6, t('errorMessage.max6Digit'))
          .max(6, t('errorMessage.max6Digit'))
      }),
    onSubmit: ({ token }: { token: string }) => {
      loginUser({
        token
      })
    }
  })

  useEffect(() => {
    if (values.token) {
      setErrorMessage('')
    }
  }, [values.token, setErrorMessage])

  return (
    <AuthView heading="Two-step verification" handleSubmit={handleSubmit}>
      {isLoading ? (
        <Loader loaderStyle="authLoader" />
      ) : (
        <Box className={styles.loginWithTwoFAContainer}>
          <Text className={styles.passwordText}>{t('2fa.enterSixDigitCode')}</Text>
          <FieldInput
            name="token"
            label={t('form.enterSixDigit')}
            placeHolder={t('form.placeholderSixDigit')}
            value={values.token}
            onChange={(a: string) => setFieldValue('token', a)}
            handleBlur={handleBlur}
            error={errors.token}
            touched={touched.token}
          />
          {errorMessage && <FieldError error={errorMessage} />}
          <PercentButton
            className={styles.loginButton}
            fullWidth
            data-testid="auth-active-button"
            title={t('button.confirm')}
            type="submit"
          />
          <Text className={`${styles.loginFooterText} ${styles.loginFooterTextFirst}`}>
            <span>
              <Link
                to="/login/2fa-recovery"
                state={{
                  ...state
                }}
              >
                {t('2fa.useRecoveryCode')}
              </Link>
            </span>
          </Text>
          <Text className={styles.loginFooterText}>
            <span>
              <Link to="/login">{t('2fa.loginDifferentUser')}</Link>
            </span>
          </Text>
        </Box>
      )}
    </AuthView>
  )
}
